import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { Button } from '@/components/ui/button';
import { KpiCard, KraBar } from '@/components/reports/KpiCard';
import { BarChart3, Users, Wallet, ClipboardList, ShieldCheck, User2, RefreshCcw } from 'lucide-react';

const DASHBOARD_LABEL = {
  owner: 'Owner Control',
  operations: 'Design Ops',
  accounts: 'Accounts',
  dm_creative: 'DM · Creative',
  dm_technical: 'DM · Technical',
  dm_architecture: 'DM · Architecture',
};

function dashboardEndpoint(role) {
  if (role === 'owner') return '/reports/kra/owner-control';
  if (role === 'operations') return '/reports/kra/ops';
  if (role === 'accounts') return '/reports/kra/accounts';
  if (role === 'dm_creative') return '/reports/kra/dm/dm_creative';
  if (role === 'dm_technical') return '/reports/kra/dm/dm_technical';
  if (role === 'dm_architecture') return '/reports/kra/dm/dm_architecture';
  return null;
}

const OWNER_SWITCHES = [
  { role: 'owner', label: 'Owner Control' },
  { role: 'operations', label: 'Design Ops' },
  { role: 'accounts', label: 'Accounts' },
  { role: 'dm_creative', label: 'DM · Creative' },
  { role: 'dm_technical', label: 'DM · Technical' },
  { role: 'dm_architecture', label: 'DM · Architecture' },
];

export default function ReportsKraPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const initial = isOwner ? 'owner' : (user?.role || 'owner');
  const [view, setView] = useState(initial);
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const endpoint = dashboardEndpoint(view);

  const load = useCallback(async () => {
    if (!endpoint) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (includeArchived) params.set('include_archived', '1');
      const url = params.toString() ? `${endpoint}?${params}` : endpoint;
      const r = await api.get(url);
      setPayload(r.data);
    } catch (e) {
      toast.error(parseErr(e));
      setPayload(null);
    } finally { setBusy(false); }
  }, [endpoint, includeArchived]);

  useEffect(() => { load(); }, [load]);

  useAutoRefetch(load, {
    collections: ['actions', 'client_approvals', 'collections_ledger', 'master_projects'],
  });

  const cards = payload?.cards || [];
  const kras = payload?.kras || [];
  const dqStrip = payload?.data_quality_strip || null;

  return (
    <AppShell>
      <div className="space-y-4" data-testid="reports-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 size={20} className="text-[hsl(var(--primary))]" />
              <h1 className="text-[22px] font-semibold" data-testid="reports-title">
                Reports · {DASHBOARD_LABEL[view] || view}
              </h1>
            </div>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-0.5">
              Read-only aggregation over Master Projects, Work Orders, Follow-ups, and QC Gates. Every card opens the filtered records behind it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[12px] text-[hsl(var(--foreground-muted))]">
              <input type="checkbox" checked={includeArchived}
                     onChange={e => setIncludeArchived(e.target.checked)}
                     data-testid="reports-include-archived" />
              Include archived
            </label>
            <Button size="sm" variant="outline" onClick={load} disabled={busy}
                    data-testid="reports-refresh">
              <RefreshCcw size={13} className="mr-1" /> Refresh
            </Button>
            <Link to="/reports/builder"
                  className="text-[12px] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] underline underline-offset-2"
                  data-testid="reports-open-builder">
              Report Builder
            </Link>
          </div>
        </div>

        {isOwner && (
          <div className="flex flex-wrap gap-1.5 border-b pb-2"
               style={{ borderColor: 'hsl(var(--border))' }}
               data-testid="reports-owner-switcher">
            {OWNER_SWITCHES.map(s => {
              const active = view === s.role;
              return (
                <button key={s.role} onClick={() => setView(s.role)}
                        data-testid={`reports-switch-${s.role}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] border transition-colors ${
                          active
                            ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]'
                            : 'border-[hsl(var(--border))] text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--surface-1))]'
                        }`}>
                  {_iconFor(s.role)} {s.label}
                </button>
              );
            })}
          </div>
        )}

        {payload?.scope && (
          <div className="text-[12px] text-[hsl(var(--foreground-muted))]" data-testid="reports-scope">
            Scope: <strong>{payload.scope.projects}</strong> projects · <strong>{payload.scope.team_size}</strong> team members
            {payload.filters_applied?.stage_remit?.length > 0 && (
              <> · stages: {payload.filters_applied.stage_remit.join(', ')}</>
            )}
          </div>
        )}

        {busy && !payload && (
          <div className="text-center py-16 text-[hsl(var(--foreground-muted))]">Loading…</div>
        )}

        {payload && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5"
                 data-testid="reports-cards">
              {cards.map((c, i) => (
                <KpiCard key={`${c.label}-${i}`} card={c}
                         testId={`reports-card-${_slug(c.label)}`} />
              ))}
            </div>

            {kras.length > 0 && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2 flex items-center gap-1.5">
                  <ShieldCheck size={15} /> KRAs · numerator / denominator
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5"
                     data-testid="reports-kras">
                  {kras.map((k, i) => (
                    <KraBar key={`${k.name}-${i}`} kra={k}
                            testId={`reports-kra-${_slug(k.name)}`} />
                  ))}
                </div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1.5">
                  <em>Client-controlled waits (waiting for client, awaiting approvals, payment delays)
                    are visible above but do not count against personal on-time scores.</em>
                </div>
              </div>
            )}

            {dqStrip && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2">Data quality strip</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2"
                     data-testid="reports-dq-strip">
                  {Object.entries(dqStrip).map(([k, c]) => (
                    <KpiCard key={k} card={c} testId={`reports-dq-${k}`} />
                  ))}
                </div>
                {isOwner && (
                  <div className="mt-2">
                    <Link to="/reports/data-quality"
                          className="text-[12.5px] text-[hsl(var(--primary))] hover:underline"
                          data-testid="reports-open-dq">
                      Open full Data Quality view →
                    </Link>
                  </div>
                )}
              </div>
            )}

            {payload.workload && payload.workload.length > 0 && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2 flex items-center gap-1.5">
                  <Users size={15} /> Resource workload
                </h2>
                <WorkloadTable rows={payload.workload} />
              </div>
            )}

            {payload.status_breakdown && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2">Collections status</h2>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2"
                     data-testid="reports-status-breakdown">
                  {Object.entries(payload.status_breakdown).map(([k, v]) => (
                    <div key={k} className="border rounded-lg p-2.5"
                         style={{ borderColor: 'hsl(var(--border))' }}>
                      <div className="text-[10.5px] uppercase tracking-wide text-[hsl(var(--foreground-muted))]">
                        {k.replace(/_/g, ' ')}
                      </div>
                      <div className="text-[18px] font-semibold tabular-nums">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {payload.lists && Object.entries(payload.lists).length > 0 && (
              <div>
                <h2 className="text-[16px] font-semibold mt-4 mb-2">Detail lists</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3"
                     data-testid="reports-lists">
                  {Object.entries(payload.lists).map(([k, rows]) => (
                    <ListPreview key={k} title={k.replace(/_/g, ' ')} rows={rows} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function _iconFor(role) {
  if (role === 'owner') return <BarChart3 size={12} />;
  if (role === 'operations') return <ClipboardList size={12} />;
  if (role === 'accounts') return <Wallet size={12} />;
  return <User2 size={12} />;
}

function _slug(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function WorkloadTable({ rows }) {
  return (
    <div className="border rounded-xl bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}>
      <table className="tbl" data-testid="reports-workload-table">
        <thead><tr><th>User</th><th>Active projects</th><th></th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.user_id}>
              <td className="font-mono text-[11.5px]">{r.user_id.slice(0, 8)}…</td>
              <td className="tabular-nums">{r.active_projects}</td>
              <td>
                {r.overloaded && (
                  <span className="chip chip-warning text-[10px]">Overloaded</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListPreview({ title, rows }) {
  if (!rows) return null;
  const arr = Array.isArray(rows) ? rows : [];
  const show = arr.slice(0, 5);
  return (
    <div className="border rounded-xl p-3 bg-[hsl(var(--card))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`reports-list-${_slug(title)}`}>
      <div className="text-[12.5px] font-semibold mb-1 capitalize">
        {title} <span className="text-[hsl(var(--foreground-subtle))]">({arr.length})</span>
      </div>
      {arr.length === 0 && (
        <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">Nothing to show.</div>
      )}
      <ul className="space-y-1">
        {show.map((r, i) => (
          <li key={r.id || i} className="text-[12px] flex items-center justify-between gap-2 border-b py-1 last:border-0"
              style={{ borderColor: 'hsl(var(--border))' }}>
            <span className="truncate">
              {r.project_name || r.title || r.pi_number || (r.id ? r.id.slice(0, 8) : JSON.stringify(r).slice(0, 60))}
            </span>
            {r.id && r.project_name && (
              <Link to={`/projects/${r.id}`} className="text-[10.5px] text-[hsl(var(--primary))] hover:underline whitespace-nowrap">
                Open →
              </Link>
            )}
            {r.project_id && r.title && (
              <Link to={`/projects/${r.project_id}`} className="text-[10.5px] text-[hsl(var(--primary))] hover:underline whitespace-nowrap">
                Open →
              </Link>
            )}
          </li>
        ))}
        {arr.length > 5 && (
          <li className="text-[10.5px] text-[hsl(var(--foreground-muted))] pt-1">
            + {arr.length - 5} more
          </li>
        )}
      </ul>
    </div>
  );
}
